/**
 * Which hub devices are WATER TANKS, and what level they report.
 *
 * The hub's unified registry already has the class — `water_tank` (controlType
 * `sensor`, category climate), whose one required value is `level_percentage` —
 * so the app must not invent a second vocabulary. The 3D-model section reads
 * this to offer the tank family to a tank (and NOT to a shade or a lamp), and
 * the live device-model layer reads it to move the water.
 *
 * The class id is the primary key (`POST /api/devices {classId}` writes it as
 * the device's type); the word hints are the fallback for hubs that only carry
 * a type string. Words are matched as words — never as a bare substring — the
 * lesson `shadeDevices.ts` records.
 */

/** The class ids that ARE a water tank, as the hub spells them. */
export const WATER_TANK_CLASS_IDS = ['water_tank', 'cistern', 'tank', 'water_storage'] as const;

/** Word hints that mark a tank in the fields a hub names devices in. */
const WATER_TANK_WORD_HINTS = ['tank', 'cistern', 'reservoir'];

/**
 * The values a tank's level can arrive under. `level_percentage` is the hub
 * class's own; the others are protocol spellings seen from KNX/Modbus imports.
 * Deliberately NOT the bare `level` alone… it IS included, because a KNX tank
 * import often names it that — a shade's `level` is a different class and the
 * class check runs first.
 */
export const WATER_TANK_LEVEL_KEYS = [
  'level_percentage',
  'level',
  'tank_level_percent_status',
  'fill_level',
  'percentage',
] as const;

export interface TankCandidate {
  classId?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceControl?: string | null;
  deviceValues?: Array<{ valueKey?: string | null; name?: string | null; currentValue?: unknown }>;
  values?: Array<{ valueKey?: string | null; name?: string | null; currentValue?: unknown }>;
}

function words(value: string | null | undefined): string[] {
  // UNICODE letters, not a-z: `čistilna` split on `[^a-z0-9]` becomes `istilna`,
  // so a Slovene device name never matched its own hint (measured).
  return (value ?? '')
    .trim()
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word !== '');
}

/** The class ids that ARE a waste-water treatment plant, as the hub spells them. */
export const WASTE_TANK_CLASS_IDS = ['waste_tank', 'septic_tank', 'treatment_plant'] as const;

/** Word hints that mark a treatment plant (never a cistern). */
const WASTE_TANK_WORD_HINTS = ['septic', 'sewage', 'waste', 'wastewater', 'cistilna', 'čistilna'];

/** True when the device is a waste-water treatment plant ("biološka čistilna naprava"). */
export function isWasteTankDevice(device: TankCandidate | null | undefined): boolean {
  if (!device) return false;
  const classId = (device.classId ?? device.deviceType ?? '').trim().toLowerCase();
  if (classId !== '' && (WASTE_TANK_CLASS_IDS as readonly string[]).includes(classId)) return true;
  const typeWords = words(device.deviceType);
  if (typeWords.some((word) => WASTE_TANK_WORD_HINTS.includes(word))) return true;
  return words(device.controlType ?? device.deviceControl).some((word) => WASTE_TANK_WORD_HINTS.includes(word));
}

/**
 * True when the device is a cistern / water tank.
 *
 * A WASTE tank is excluded first: its class spells `waste_tank`, whose word
 * `tank` would otherwise match the cistern's own hints and offer the house's
 * drinking-water cistern to a sewage plant (the panel's list is built from this
 * kind, so the two matchers must be mutually exclusive).
 */
export function isWaterTankDevice(device: TankCandidate | null | undefined): boolean {
  if (!device) return false;
  if (isWasteTankDevice(device)) return false;
  const classId = (device.classId ?? device.deviceType ?? '').trim().toLowerCase();
  if (classId !== '' && (WATER_TANK_CLASS_IDS as readonly string[]).includes(classId)) return true;
  const typeWords = words(device.deviceType);
  if (typeWords.some((word) => WATER_TANK_WORD_HINTS.includes(word))) return true;
  return words(device.controlType ?? device.deviceControl).some((word) => WATER_TANK_WORD_HINTS.includes(word));
}

function tankValues(device: TankCandidate): NonNullable<TankCandidate['deviceValues']> {
  if (Array.isArray(device.deviceValues)) return device.deviceValues;
  return Array.isArray(device.values) ? device.values : [];
}

/**
 * The tank's level in percent, or null when it reports nothing usable.
 *
 * A reading outside 0…100 is clamped rather than rejected: KNX tanks send
 * DPT5.001 scaled values and an out-of-range byte (255 = "invalid") must not
 * raise a 255 % water line through the tank's lid.
 *
 * `liveValues` is the socket-fed overlay a card holds (`useDeviceValues`): the
 * device prop can lag a push by a tick, and the picture beside the number should
 * move with the number, not one reading behind it.
 */
export function getTankLevelPct(
  device: TankCandidate | null | undefined,
  liveValues?: Record<string, unknown> | null,
): number | null {
  if (!device) return null;
  const keys = new Set(WATER_TANK_LEVEL_KEYS.map((key) => key.toLowerCase()));
  for (const row of tankValues(device)) {
    const key = (row.valueKey ?? '').trim().toLowerCase();
    const name = (row.name ?? '').trim().toLowerCase();
    if (!keys.has(key) && !keys.has(name)) continue;
    const live = liveValues && row.valueKey ? liveValues[row.valueKey] : undefined;
    const raw = live !== undefined ? live : row.currentValue;
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    if (!Number.isFinite(value)) continue;
    return Math.min(100, Math.max(0, value));
  }
  return null;
}
